import React, { useEffect, useState } from "react";
import clsx from "clsx";
import format from "date-fns/format";
import isBefore from "date-fns/isBefore";
import parseISO from "date-fns/parseISO";
import startOfToday from "date-fns/startOfToday";

import Button from "@material-ui/core/Button";
import MenuItem from "@material-ui/core/MenuItem";
import Paper from "@material-ui/core/Paper";
import Select from "@material-ui/core/Select";
import { Theme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Launch from "@material-ui/icons/Launch";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import { ConfirmationContextProvider } from "components/ConfirmationDialog/ConfirmationContext";
import Loader from "components/Loader";
import Pagination from "components/Pagination";
import PaperCarousel from "components/PaperCarousel";
import { TabTable } from "components/TabTables/TabTables";
import { useRacehorse360Api } from "hooks/api";
import { SortOrder } from "interfaces/SortOrder";
import InjectionFormModal from "./InjectionFormModal";
import VaccinationDetailsModal from "./VaccinationDetailsModal";
import { getDaysAmountToExpirationDate } from "./helper";

import useStyles from "./styles";

export enum VaccinationStatus {
  ALL_STATUSES = "All Statuses",
  ACTIVE = "Active",
  EXPIRING_SOON = "Expiring Soon",
  EXPIRED = "Expired"
}

interface IProps extends TabTable {
  horse: racehorse360.IHorse;
}

const PAGE_SIZE = 8;
const SLIDE_SIZE = 4;

const Vaccinations = (props: IProps) => {
  const { horse, onLoad } = props;

  const classes = useStyles();
  const matchesDownSm = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );

  const [activeFilter, setActiveFilter] = useState<VaccinationStatus>(
    VaccinationStatus.ALL_STATUSES
  );
  const [activeVaccinationId, setActiveVaccinationId] =
    useState<racehorse360.IVaccination>();
  const [isDetailsOpen, setIsDetailsOpen] = useState<boolean>(false);
  const [activePage, setActivePage] = useState<number>(1);
  const [activeSlide, setActiveSlide] = useState<number>(0);

  const { useListInjections } = useRacehorse360Api();

  const {
    data: listVaccineInjectionsData,
    isLoading: isListVaccineInjectionsLoading
  } = useListInjections(
    `${horse?.id}-list-vaccines`,
    {
      query: {
        horseIds: [horse?.id],
        medicineTypes: [racehorse360.MedicineType.MEDICINE_TYPE_VACCINE]
      },
      pagingOptions: {
        maxResults: 9999,
        includeSummary: true
      },
      getOptions: {
        select: [
          "id",
          "medicineId",
          "medicine.name",
          "vaccinationId",
          "injectionDate",
          "dataSource",
          "manufacturer",
          "vaccination.horseId",
          "vaccination.expirationDate",
          "createdOn",
          "updatedOn",
          "createdBy.firstName",
          "createdBy.lastName",
          "updatedBy.firstName",
          "updatedBy.lastName"
        ],
        orderBy: [`createdOn ${SortOrder.DESC}`]
      }
    },
    {
      enabled: Boolean(horse?.id),
      onSuccess: data => {
        onLoad && onLoad(data.injections, data.pagingInfo);
      },
      initialData: {
        injections: []
      }
    }
  );
  const injections = listVaccineInjectionsData?.injections;

  const filterVaccinations = (injections = []) => {
    const sortFn = (a, b) => {
      const differenceA = getDaysAmountToExpirationDate(
        a.expirationDate,
        Date.now()
      );
      const differenceB = getDaysAmountToExpirationDate(
        b.expirationDate,
        Date.now()
      );

      return differenceB - differenceA;
    };
    let filterFn: (item) => boolean;

    switch (activeFilter) {
      case VaccinationStatus.ACTIVE:
        filterFn = item => {
          const expirationDate = item.vaccination.expirationDate;

          return [
            expirationDate,
            getDaysAmountToExpirationDate(expirationDate) >= 14
          ].every(Boolean);
        };

        break;
      case VaccinationStatus.EXPIRING_SOON:
        filterFn = item => {
          const expirationDate = item.vaccination.expirationDate;
          const difference = getDaysAmountToExpirationDate(expirationDate);

          return [expirationDate, difference < 14, difference >= 0].every(
            Boolean
          );
        };

        break;
      case VaccinationStatus.EXPIRED:
        filterFn = item => {
          const expirationDate = item.vaccination.expirationDate;

          return [
            expirationDate,
            isBefore(parseISO(item.vaccination.expirationDate), startOfToday())
          ].every(Boolean);
        };

        break;
      case VaccinationStatus.ALL_STATUSES:
        return injections;
    }

    return injections.filter(filterFn).sort(sortFn);
  };

  const vaccinations = filterVaccinations(injections);
  const activeVaccine = vaccinations.find(v => v.id === activeVaccinationId);

  const handleSlideChange = (index: number) => {
    setActiveSlide(index);
  };

  const handlePageChange = (pageNumber: number) => {
    setActivePage(pageNumber);
  };

  const handleFilterChange = event => {
    setActivePage(1);
    setActiveSlide(0);
    setActiveFilter(event.target.value);
  };

  const handleItemClick = vaccinationId => () => {
    setActiveVaccinationId(vaccinationId);
    setIsDetailsOpen(true);
  };

  const handleDetailsClose = () => {
    setIsDetailsOpen(false);
  };

  const renderNoVaccinationData = () => {
    return <div className={classes.noItems}>No Vaccine Records</div>;
  };

  const renderDates = item =>
    [item.injectionDate, item.vaccination?.expirationDate]
      .filter(Boolean)
      .map(date => format(parseISO(date), "M/dd/yy"))
      .join(" - ");

  const renderStatus = item => {
    const difference = getDaysAmountToExpirationDate(
      item.vaccination.expirationDate
    );
    const className = [classes.itemStatus];
    let status = "Unknown";

    if (difference >= 14) {
      className.push(classes.itemStatusActive);
      status = "Active";
    } else if (difference >= 0) {
      className.push(classes.itemStatusExpiring);
      status = "Expiring Soon";
    } else if (difference < 0) {
      className.push(classes.itemStatusExpired);
      status = "Expired";
    }
    return (
      <span className={clsx(classes.itemStatus, ...className)}>{status}</span>
    );
  };

  const renderVaccinationItem = (item: racehorse360.IInjection) => {
    return (
      <Button
        key={item.id}
        className={classes.itemButton}
        variant="text"
        onClick={handleItemClick(item.id)}
      >
        <div className={classes.item}>
          <div className={classes.itemData}>
            <p className={classes.itemName}>{item.medicine.name}</p>
            <span className={classes.itemDates}>{renderDates(item)}</span>
            {renderStatus(item)}
          </div>
          <Launch className={classes.launchIcon} />
        </div>
      </Button>
    );
  };

  const renderHeader = () => (
    <div className={classes.header}>
      <div className={classes.filterWrapper}>
        Display
        <Select
          className={classes.filterSelect}
          value={activeFilter}
          onChange={handleFilterChange}
          displayEmpty
        >
          <MenuItem value={VaccinationStatus.ALL_STATUSES}>
            {VaccinationStatus.ALL_STATUSES}
          </MenuItem>
          <MenuItem value={VaccinationStatus.ACTIVE}>
            {VaccinationStatus.ACTIVE}
          </MenuItem>
          <MenuItem value={VaccinationStatus.EXPIRING_SOON}>
            {VaccinationStatus.EXPIRING_SOON}
          </MenuItem>
          <MenuItem value={VaccinationStatus.EXPIRED}>
            {VaccinationStatus.EXPIRED}
          </MenuItem>
        </Select>
      </div>
      <div>
        <InjectionFormModal
          horse={horse}
          type={racehorse360.MedicineType.MEDICINE_TYPE_VACCINE}
        />
      </div>
    </div>
  );

  const renderDesktopContent = () => {
    if (!vaccinations.length) return renderNoVaccinationData();
    return isListVaccineInjectionsLoading ? (
      <Loader />
    ) : (
      vaccinations
        .slice(activePage * PAGE_SIZE - PAGE_SIZE, activePage * PAGE_SIZE)
        .map(renderVaccinationItem)
    );
  };

  const renderCarouselItem = (items: racehorse360.IVaccination[]) => {
    return isListVaccineInjectionsLoading ? (
      <Loader />
    ) : (
      items.map(renderVaccinationItem)
    );
  };

  const renderMobileView = () => {
    const start = vaccinations.length ? activeSlide * SLIDE_SIZE + 1 : 0;
    const end = (activeSlide + 1) * SLIDE_SIZE;
    const hasVaccineInjections = Boolean(
      listVaccineInjectionsData?.pagingInfo?.totalResults
    );
    const title = hasVaccineInjections ? "Vaccinations" : "No Vaccinations";
    const slideNumberInfo = `Showing ${start} - ${
      end < vaccinations.length ? end : vaccinations.length
    } of ${vaccinations.length}`;

    return (
      <div>
        <div className={classes.mobileHeader}>
          <span className={classes.mobileTitle}>{title}</span>
          <span>{slideNumberInfo}</span>
        </div>
        {renderHeader()}
        {!vaccinations.length && hasVaccineInjections && (
          <Paper className={classes.filler}>{renderNoVaccinationData()}</Paper>
        )}
        {Boolean(vaccinations.length) && (
          <PaperCarousel
            items={vaccinations.reduce((acc, val, i) => {
              const idx = Math.floor(i / SLIDE_SIZE);
              const page = acc[idx] || (acc[idx] = []);
              page.push(val);
              return acc;
            }, [])}
            onSlideChange={handleSlideChange}
            itemRenderer={renderCarouselItem}
            activeSlide={activeSlide}
          />
        )}
      </div>
    );
  };

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(vaccinations.length / PAGE_SIZE));

    setActivePage(prevActivePage => Math.min(prevActivePage, totalPages));
  }, [vaccinations.length]);

  return (
    <ConfirmationContextProvider>
      {matchesDownSm ? (
        renderMobileView()
      ) : (
        <div className={classes.root}>
          {renderHeader()}
          <div className={classes.body}>{renderDesktopContent()}</div>
          <Pagination
            className={classes.pagination}
            totalCount={vaccinations.length}
            onPageChange={handlePageChange}
            pageSize={PAGE_SIZE}
            pageNumber={activePage}
          />
        </div>
      )}
      <VaccinationDetailsModal
        horse={horse}
        injection={activeVaccine}
        onClose={handleDetailsClose}
        open={isDetailsOpen}
      />
    </ConfirmationContextProvider>
  );
};

export default Vaccinations;
